import React, { useEffect, useMemo, useState } from "react";
import { FaFilter, FaRupeeSign } from "react-icons/fa";
import {
  FiArrowDownLeft,
  FiArrowUpRight,
  FiX,
} from "react-icons/fi";
import { useGetCashFlowReportQuery } from "../toolkitServices/cashFlowApi";
import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";

const fmt2 = (value) => Number(value || 0).toFixed(2);
const MODES = ["Cash", "Bank", "UPI", "Wallet"];

const formatEntryDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-IN");
};

export default function CashFlowReport() {
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [mode, setMode] = useState("");
  const [refNo, setRefNo] = useState("");
  const [branchId, setBranchId] = useState("");

  const [draftFrom, setDraftFrom] = useState("");
  const [draftTo, setDraftTo] = useState("");
  const [draftMode, setDraftMode] = useState("");
  const [draftRefNo, setDraftRefNo] = useState("");
  const [draftBranchId, setDraftBranchId] = useState("");

  const { data, isFetching, isError, error } = useGetCashFlowReportQuery({
    from,
    to,
    mode,
    refNo,
    branchId,
  });

  const { data: branches } = useGetAllBranchesQuery();

  const branchOptions = useMemo(
    () =>
      (branches?.data || []).map((branch) => ({
        label: branch.branchName || branch.name || "Branch",
        value: branch._id || branch.id,
      })),
    [branches],
  );

  const summary = data?.summary || {};
  const entries = data?.entries || [];

  const totalIn = MODES.reduce(
    (total, paymentMode) => total + Number(summary?.[paymentMode]?.IN || 0),
    0,
  );
  const totalOut = MODES.reduce(
    (total, paymentMode) => total + Number(summary?.[paymentMode]?.OUT || 0),
    0,
  );
  const netAmount = totalIn - totalOut;

  const activeFilters = useMemo(() => {
    const filters = [];
    if (from) filters.push({ key: "from", label: `From: ${from}` });
    if (to) filters.push({ key: "to", label: `To: ${to}` });
    if (mode) filters.push({ key: "mode", label: `Mode: ${mode}` });
    if (refNo) filters.push({ key: "refNo", label: `Reference: ${refNo}` });
    if (branchId) {
      filters.push({
        key: "branch",
        label: `Branch: ${
          branchOptions.find((branch) => branch.value === branchId)?.label ||
          "Selected"
        }`,
      });
    }
    return filters;
  }, [branchId, branchOptions, from, mode, refNo, to]);

  const openFilters = () => {
    setDraftFrom(from);
    setDraftTo(to);
    setDraftMode(mode);
    setDraftRefNo(refNo);
    setDraftBranchId(branchId);
    setIsFilterOpen(true);
  };

  const clearDraftFilters = () => {
    setDraftFrom("");
    setDraftTo("");
    setDraftMode("");
    setDraftRefNo("");
    setDraftBranchId("");
  };

  const clearAppliedFilters = () => {
    setFrom("");
    setTo("");
    setMode("");
    setRefNo("");
    setBranchId("");
  };

  const applyFilters = (event) => {
    event.preventDefault();
    if (draftFrom && draftTo && draftFrom > draftTo) return;

    setFrom(draftFrom);
    setTo(draftTo);
    setMode(draftMode);
    setRefNo(draftRefNo.trim());
    setBranchId(draftBranchId);
    setIsFilterOpen(false);
  };

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsFilterOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, []);

  const hasInvalidDateRange = Boolean(
    draftFrom && draftTo && draftFrom > draftTo,
  );

  return (
    <div className="flex h-[calc(100vh-140px)] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg md:h-[calc(100vh-70px)]">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between gap-3 bg-gradient-to-r from-[#6b59c6] via-[#806dcc] to-[#9481ce] px-3 py-2.5 text-white shadow-sm sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/20">
            <FaRupeeSign className="text-sm" />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold tracking-tight sm:text-lg">
              Cash Flow Report
            </h2>
            <p className="truncate text-[9px] text-violet-100 sm:text-[11px]">
              Cash • Bank • UPI • Wallet
            </p>
          </div>
          {isFetching && (
            <span className="hidden items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[10px] sm:inline-flex">
              <span className="h-2.5 w-2.5 animate-spin rounded-full border border-white border-t-transparent" />
              refreshing…
            </span>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span className="hidden rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-medium ring-1 ring-white/10 sm:inline-flex">
            {entries.length} entries
          </span>
          <button
            type="button"
            onClick={openFilters}
            className="inline-flex h-9 w-9 items-center justify-center gap-1.5 rounded-xl bg-white/15 text-white ring-1 ring-white/20 transition hover:bg-white/25 sm:w-auto sm:rounded-full sm:px-3"
            title="Filters"
            aria-label="Open filters"
          >
            <FaFilter className="text-xs" />
            <span className="hidden text-[11px] font-semibold sm:inline">
              Filters
            </span>
          </button>
        </div>
      </div>

      {/* Active filters */}
      {activeFilters.length > 0 && (
        <div className="flex shrink-0 items-center gap-2 border-b bg-slate-50 px-3 py-1.5 sm:px-4">
          <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {activeFilters.map((filter) => (
              <span
                key={filter.key}
                className="shrink-0 rounded-full border border-violet-100 bg-white px-2.5 py-1 text-[10px] font-medium text-slate-600"
              >
                {filter.label}
              </span>
            ))}
          </div>
          <button
            type="button"
            onClick={clearAppliedFilters}
            className="shrink-0 text-[10px] font-semibold text-[#6b59c6] hover:underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Entries */}
      <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/70 p-2.5 sm:p-4">
        {isFetching && entries.length === 0 ? (
          <EmptyState loading />
        ) : isError ? (
          <EmptyState
            message={error?.data?.message || "Unable to load cash flow entries"}
            error
          />
        ) : entries.length === 0 ? (
          <EmptyState message="No cash flow entries found" />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm md:block">
              <table className="w-full text-left text-xs">
                <thead className="sticky top-0 z-10 bg-[#efeafd] text-[10px] uppercase tracking-wide text-[#4b3d91]">
                  <tr>
                    <th className="px-3 py-2.5">Date</th>
                    <th className="px-3 py-2.5">Branch</th>
                    <th className="px-3 py-2.5">Mode</th>
                    <th className="px-3 py-2.5">Source</th>
                    <th className="px-3 py-2.5">Reference</th>
                    <th className="border-l-2 border-l-emerald-200 bg-emerald-50/70 px-3 py-2.5 text-right text-emerald-700">
                      IN
                    </th>
                    <th className="border-l border-l-rose-100 bg-rose-50/70 px-3 py-2.5 text-right text-rose-700">
                      OUT
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100 text-slate-700">
                  {entries.map((entry, index) => (
                    <tr
                      key={entry._id || `${entry.entryDate}-${index}`}
                      className="transition hover:bg-violet-50/50"
                    >
                      <td className="whitespace-nowrap px-3 py-2.5 font-medium text-slate-800">
                        {formatEntryDate(entry.entryDate)}
                      </td>
                      <td className="max-w-[180px] px-3 py-2.5">
                        <span className="block truncate">
                          {entry.branchId?.branchName || "-"}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <ModeBadge mode={entry.mode} />
                      </td>
                      <td className="max-w-[180px] px-3 py-2.5">
                        <span className="block truncate">{entry.source || "-"}</span>
                      </td>
                      <td className="max-w-[220px] px-3 py-2.5">
                        <span className="block truncate" title={entry.refNo || entry.description || "-"}>
                          {entry.refNo || entry.description || "-"}
                        </span>
                      </td>
                      <td className="border-l-2 border-l-emerald-100 bg-emerald-50/30 px-3 py-2.5 text-right font-semibold tabular-nums text-emerald-700">
                        {entry.flow === "IN" ? `₹ ${fmt2(entry.amount)}` : "-"}
                      </td>
                      <td className="border-l border-l-rose-100 bg-rose-50/30 px-3 py-2.5 text-right font-semibold tabular-nums text-rose-700">
                        {entry.flow === "OUT" ? `₹ ${fmt2(entry.amount)}` : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile list */}
            <div className="space-y-2 md:hidden">
              {entries.map((entry, index) => {
                const isIn = entry.flow === "IN";
                return (
                  <article
                    key={entry._id || `${entry.entryDate}-${index}`}
                    className={`overflow-hidden rounded-xl border bg-white shadow-sm ${
                      isIn ? "border-emerald-100" : "border-rose-100"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 px-3 py-2.5">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-slate-800">
                          {formatEntryDate(entry.entryDate)}
                        </p>
                        <p className="mt-0.5 truncate text-[10px] text-slate-500">
                          {entry.branchId?.branchName || "-"}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <ModeBadge mode={entry.mode} />
                        <span
                          className={`rounded-full px-2 py-1 text-[9px] font-bold ${
                            isIn
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-rose-50 text-rose-700"
                          }`}
                        >
                          {isIn ? "IN" : "OUT"}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-[65px_1fr] gap-y-1 border-t border-slate-100 px-3 py-2 text-[10px]">
                      <span className="font-medium text-slate-400">Source</span>
                      <span className="truncate text-slate-700">
                        {entry.source || "-"}
                      </span>
                      <span className="font-medium text-slate-400">Reference</span>
                      <span className="truncate text-slate-700">
                        {entry.refNo || entry.description || "-"}
                      </span>
                    </div>

                    <div
                      className={`flex items-center justify-between border-t px-3 py-2 ${
                        isIn
                          ? "border-emerald-100 bg-emerald-50/50"
                          : "border-rose-100 bg-rose-50/50"
                      }`}
                    >
                      <span className="text-[10px] font-medium text-slate-500">
                        Amount
                      </span>
                      <span
                        className={`text-sm font-bold tabular-nums ${
                          isIn ? "text-emerald-700" : "text-rose-700"
                        }`}
                      >
                        {isIn ? "+" : "−"} ₹ {fmt2(entry.amount)}
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Summary */}
      {entries.length > 0 && (
        <div className="shrink-0 border-t bg-white px-2.5 py-2 sm:px-4 sm:py-2.5">
          <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {MODES.map((paymentMode) => (
              <CompactSummaryChip
                key={paymentMode}
                label={paymentMode}
                data={summary?.[paymentMode]}
              />
            ))}
          </div>

          <div className="mt-2 grid grid-cols-3 gap-2 text-center">
            <TotalCard label="Total In" value={totalIn} tone="in" />
            <TotalCard label="Total Out" value={totalOut} tone="out" />
            <TotalCard label="Net Flow" value={netAmount} tone="net" />
          </div>
        </div>
      )}

      {/* Filter panel */}
      {isFilterOpen && (
        <div
          className="fixed inset-0 z-[9998] flex items-end justify-center bg-slate-950/40 p-3 backdrop-blur-[1px] sm:items-center"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsFilterOpen(false);
          }}
        >
          <form
            onSubmit={applyFilters}
            className="w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b bg-gradient-to-r from-[#6b59c6] to-[#9481ce] px-4 py-3 text-white">
              <div>
                <h3 className="text-sm font-semibold">Cash Flow Filters</h3>
                <p className="mt-0.5 text-[10px] text-violet-100">
                  Filter by period, branch, payment mode or reference.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsFilterOpen(false)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15 transition hover:bg-white/25"
                aria-label="Close filters"
              >
                <FiX />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
              <FilterField label="From date">
                <input
                  type="date"
                  value={draftFrom}
                  onChange={(event) => setDraftFrom(event.target.value)}
                  className="filter-input"
                />
              </FilterField>

              <FilterField label="To date">
                <input
                  type="date"
                  value={draftTo}
                  onChange={(event) => setDraftTo(event.target.value)}
                  className="filter-input"
                />
              </FilterField>

              <FilterField label="Branch">
                <select
                  value={draftBranchId}
                  onChange={(event) => setDraftBranchId(event.target.value)}
                  className="filter-input"
                >
                  <option value="">All branches</option>
                  {branchOptions.map((branch) => (
                    <option key={branch.value} value={branch.value}>
                      {branch.label}
                    </option>
                  ))}
                </select>
              </FilterField>

              <FilterField label="Payment mode">
                <select
                  value={draftMode}
                  onChange={(event) => setDraftMode(event.target.value)}
                  className="filter-input"
                >
                  <option value="">All modes</option>
                  {MODES.map((paymentMode) => (
                    <option key={paymentMode} value={paymentMode}>
                      {paymentMode}
                    </option>
                  ))}
                </select>
              </FilterField>

              <div className="sm:col-span-2">
                <FilterField label="Reference number">
                  <input
                    type="text"
                    value={draftRefNo}
                    onChange={(event) => setDraftRefNo(event.target.value)}
                    placeholder="Search reference or description"
                    className="filter-input"
                  />
                </FilterField>
              </div>

              {hasInvalidDateRange && (
                <p className="text-[10px] font-medium text-rose-600 sm:col-span-2">
                  The From date must be earlier than or equal to the To date.
                </p>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 border-t bg-slate-50 px-4 py-3">
              <button
                type="button"
                onClick={clearDraftFilters}
                className="text-[11px] font-semibold text-slate-500 hover:text-slate-700"
              >
                Reset
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsFilterOpen(false)}
                  className="rounded-full border border-slate-200 bg-white px-4 py-2 text-[11px] font-semibold text-slate-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={hasInvalidDateRange}
                  className="rounded-full bg-[#6b59c6] px-5 py-2 text-[11px] font-semibold text-white shadow-sm transition hover:bg-[#5948ae] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function EmptyState({ loading = false, error = false, message }) {
  return (
    <div className="flex h-full min-h-[180px] flex-col items-center justify-center text-center">
      <span
        className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl ${
          error ? "bg-rose-50 text-rose-500" : "bg-violet-50 text-[#6b59c6]"
        }`}
      >
        {loading ? (
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : (
          <FaRupeeSign className="text-lg" />
        )}
      </span>
      <p className={`mt-3 text-xs ${error ? "text-rose-600" : "text-slate-500"}`}>
        {loading ? "Loading cash flow…" : message}
      </p>
    </div>
  );
}

function ModeBadge({ mode }) {
  return (
    <span className="inline-flex rounded-full bg-violet-50 px-2 py-1 text-[9px] font-semibold text-[#6b59c6] ring-1 ring-violet-100">
      {mode || "-"}
    </span>
  );
}

function CompactSummaryChip({ label, data }) {
  return (
    <div className="min-w-[132px] rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-2">
      <p className="text-[10px] font-semibold text-slate-500">{label}</p>
      <div className="mt-1 flex items-center justify-between gap-3 text-[10px] tabular-nums">
        <span className="inline-flex items-center gap-1 font-semibold text-emerald-700">
          <FiArrowDownLeft /> ₹ {fmt2(data?.IN)}
        </span>
        <span className="inline-flex items-center gap-1 font-semibold text-rose-700">
          <FiArrowUpRight /> ₹ {fmt2(data?.OUT)}
        </span>
      </div>
    </div>
  );
}

function TotalCard({ label, value, tone }) {
  const tones = {
    in: "border-emerald-100 bg-emerald-50 text-emerald-700",
    out: "border-rose-100 bg-rose-50 text-rose-700",
    net: "border-violet-100 bg-violet-50 text-[#4b3d91]",
  };

  return (
    <div className={`min-w-0 rounded-xl border px-2 py-2 ${tones[tone]}`}>
      <p className="truncate text-[9px] font-semibold uppercase tracking-wide opacity-80">
        {label}
      </p>
      <p className="mt-0.5 truncate text-[11px] font-bold tabular-nums sm:text-sm">
        ₹ {fmt2(value)}
      </p>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <label className="block min-w-0 text-[10px] font-medium text-slate-600 [&_.filter-input]:mt-1 [&_.filter-input]:h-9 [&_.filter-input]:w-full [&_.filter-input]:rounded-lg [&_.filter-input]:border [&_.filter-input]:border-slate-200 [&_.filter-input]:bg-white [&_.filter-input]:px-2.5 [&_.filter-input]:text-[11px] [&_.filter-input]:text-slate-700 [&_.filter-input]:outline-none [&_.filter-input:focus]:border-violet-300 [&_.filter-input:focus]:ring-1 [&_.filter-input:focus]:ring-violet-100">
      {label}
      {children}
    </label>
  );
}
